import {
  Button,
  Drawer,
  Form,
  Grid,
  Input,
  Space,
  Upload,
  getValueFromEvent,
  notification,
} from '@pankod/refine-antd';
import { file2Base64 } from '@pankod/refine-core';
import { EStatus } from 'enums';
import { IPurchase } from 'interfaces';
import { useEffect, useState } from 'react';
import { updatePurchaseStatus } from 'services/purchase';
import {
  beforeReceiptUploadValidation,
  isLessThanSize,
  isPurchaseReceiptFile,
  MAX_UPLOAD_FILE_SIZE_MB,
} from 'utilities/file';

type PurchaseReceiptDrawerProps = {
  title: string;
  visible: boolean;
  record: IPurchase;
  onClose: () => void;
  onStatusUpdate: () => Promise<void>;
};

export const PurchaseReceiptDrawer: React.FC<PurchaseReceiptDrawerProps> = (props) => {
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const [form] = Form.useForm();
  const breakpoint = Grid.useBreakpoint();

  useEffect(() => {
    form.setFieldsValue({
      orderId: props.record.id,
      supplierName: props.record.supplier.name,
      phoneNumber: props.record.supplier.mobileNumber,
      totalAmount: props.record.totalAmount,
    });
  }, [props.record, form]);

  const onFinish = async (values: any) => {
    setIsLoading(true);

    const { orderId, receipts } = values;
    const base64Files = [];

    if (receipts && receipts.length) {
      for (const file of receipts) {
        const format = file.type || '';
        const size = file.size || 0;
        if (isPurchaseReceiptFile(format) && isLessThanSize(size, MAX_UPLOAD_FILE_SIZE_MB)) {
          if (file.originFileObj) {
            const base64String = await file2Base64(file);
            const { originFileObj, ...rest } = file;
            base64Files.push({ ...rest, base64String });
          } else {
            base64Files.push(file);
          }
        }
      }
    }

    try {
      await updatePurchaseStatus(orderId, EStatus.COMPLETED, base64Files);
      props.onStatusUpdate();
      notification.success({
        message: 'Success',
        description: 'Successfully changed status',
      });
    } catch (err) {
      notification.error({
        message: 'Failed',
        description: err instanceof Error ? err.message : null,
      });
    }
    props.onClose();
    form.setFieldsValue({ receipts: [] });
    setIsLoading(false);
  };

  return (
    <Drawer
      title={props.title}
      placement="right"
      width={breakpoint.sm ? '450px' : '100%'}
      visible={props.visible}
      onClose={props.onClose}
    >
      <Form form={form} layout="vertical" onFinish={onFinish} autoComplete="off">
        <Space
          direction="vertical"
          style={{
            width: '100%',
            textAlign: 'center',
            marginBottom: '1rem',
            marginTop: '1rem',
          }}
        >
          <Form.Item name="receipts" valuePropName="fileList" getValueFromEvent={getValueFromEvent}>
            <Upload.Dragger
              listType="picture"
              accept="image/jpeg, image/png, image/heic, image/heif, .heic, .heif, application/pdf"
              multiple
              maxCount={5}
              beforeUpload={beforeReceiptUploadValidation}
            >
              <p className="ant-upload-text">Drag & Drop</p>
              <p className="ant-upload-text">Image or PDF files in this area</p>
              <p className="ant-upload-hint">
                {`Files must be less than ${MAX_UPLOAD_FILE_SIZE_MB}MB and maximum file count is 5`}
              </p>
            </Upload.Dragger>
          </Form.Item>
        </Space>

        <Form.Item label="Order ID" name="orderId">
          <Input disabled={true} />
        </Form.Item>
        <Form.Item label="Supplier Name" name="supplierName">
          <Input disabled={true} />
        </Form.Item>
        <Form.Item label="Phone Number" name="phoneNumber">
          <Input disabled={true} />
        </Form.Item>
        <Form.Item label="Amount" name="totalAmount">
          <Input disabled={true} />
        </Form.Item>
        <Form.Item wrapperCol={{ span: 4, offset: 20 }}>
          <Button loading={isLoading} type="primary" htmlType="submit">
            Save
          </Button>
        </Form.Item>
      </Form>
    </Drawer>
  );
};
